import axios from "axios";
import { getAddress, isAddressEqual, type Address } from "viem";

const API = "https://api-v2.pendle.finance/core";

// Fixed response type
type PendleSwapResponse = {
    method: string;
    contractCallParamsName: string[];
    contractCallParams: any[];
    tx: {
        data: string;
        to: string;
        from: string;
        value: string;
    };
    tokenApprovals: [
        {
            token: string;
            amount: string;
        },
    ];
    data: {
        amountOut: string;
        priceImpact: number;
        impliedApy: {
            before: number;
            after: number;
        };
        effectiveApy: number;
    };
};

// Define type for market data
type PendleMarket = {
    pt: string;
    yt: string;
    sy: string;
    underlyingAsset: string;
    address: Address;
};

export type PendleAggregator = "kyberswap" | "okx" | "odos" | "paraswap";

export abstract class PendleService {
    /**
     * Fetch active markets from Pendle API
     * @param chainId - Chain ID to get markets for
     * @returns Array of active markets
     */
    static getActiveMarkets = async (chainId: number): Promise<PendleMarket[]> => {
        try {
            const marketsResponse = await axios.get<{
                markets: PendleMarket[];
            }>(`${API}/v1/${chainId}/markets/active`);

            return marketsResponse.data.markets;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                console.error("Pendle API error fetching markets:", {
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    data: error.response?.data,
                });
                throw new Error(
                    `Failed to fetch Pendle markets: ${error.response?.status} - ${error.response?.statusText || error.message}`,
                );
            }
            throw error;
        }
    };

    /**
     * Find market address for a given token
     * @param markets - List of markets to search
     * @param tokenAddress - Token address to find market for
     * @returns Market address if found, undefined otherwise
     */
    static findMarketForToken = (markets: PendleMarket[], tokenAddress: string): Address | undefined => {
        try {
            // Convert tokenAddress to checksum address
            const checksumTokenAddress = getAddress(tokenAddress as `0x${string}`);

            for (const { pt, yt, sy, underlyingAsset, address } of markets) {
                try {
                    // Extract addresses from various formats
                    const ptAddress = pt.includes("-") ? getAddress(pt.toLowerCase().split("-")[1]) : getAddress(pt);
                    const ytAddress = yt.includes("-") ? getAddress(yt.toLowerCase().split("-")[1]) : getAddress(yt);
                    const syAddress = sy.includes("-") ? getAddress(sy.toLowerCase().split("-")[1]) : getAddress(sy);
                    const underlyingAssetAddress = underlyingAsset.includes("-")
                        ? getAddress(underlyingAsset.toLowerCase().split("-")[1])
                        : getAddress(underlyingAsset);

                    // Find matching market
                    if (
                        isAddressEqual(ptAddress, checksumTokenAddress) ||
                        isAddressEqual(ytAddress, checksumTokenAddress) ||
                        isAddressEqual(syAddress, checksumTokenAddress) ||
                        isAddressEqual(underlyingAssetAddress, checksumTokenAddress)
                    ) {
                        return address;
                    }
                } catch (error) {
                    console.warn("Failed to parse market addresses:", error);
                }
            }
            return undefined;
        } catch (error) {
            console.warn("Invalid token address format:", error);
            return undefined;
        }
    };

    /**
     * Execute a swap via Pendle API
     */
    static swap = async (params: {
        chainId: number;
        receiver: string;
        slippage: number;
        enableAggregator: boolean;
        aggregators: PendleAggregator[];
        tokenIn: string;
        tokenOut: string;
        amountIn: bigint;
        marketAddress: Address;
    }): Promise<PendleSwapResponse> => {
        try {
            const swapUrl = `${API}/v2/sdk/${params.chainId}/markets/${params.marketAddress}/swap`;

            // Create query parameters exactly matching the format in the working URL
            const queryParams = {
                receiver: params.receiver,
                slippage: typeof params.slippage === "number" ? params.slippage.toFixed(2) : String(params.slippage),
                enableAggregator: params.enableAggregator ? "true" : "false",
                aggregators: params.aggregators.join(","),
                tokenIn: params.tokenIn.toLowerCase(),
                tokenOut: params.tokenOut.toLowerCase(),
                amountIn: params.amountIn.toString(),
            };

            // Log full request details for debugging
            // console.debug("Pendle swap request:", {
            //     url: swapUrl,
            //     params: queryParams,
            // });

            // Make the request
            const swapResponse = await axios.get<PendleSwapResponse>(swapUrl, {
                params: queryParams,
                headers: {
                    Accept: "application/json",
                },
                paramsSerializer: {
                    serialize: (params) => {
                        return Object.entries(params)
                            .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
                            .join("&");
                    },
                },
            });

            return swapResponse.data;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                console.error("Pendle API error:", {
                    status: error.response?.status,
                    statusText: error.response?.statusText,
                    data: error.response?.data,
                    url: error.config?.url,
                    params: error.config?.params,
                });
                throw new Error(
                    `Failed to execute Pendle swap: ${error.response?.status} - ${error.response?.statusText || error.message}`,
                );
            }
            throw error;
        }
    };
}
